import type { ErrorResponse } from '@apollo/client/link/error';

import { deleteTokens, getTokens } from './manage_tokens';

type Listener = () => void;

/**
 * What Nest throws when a guard returns `false`.
 *
 * The API's AuthGuard and AdminGuard do not raise a 401 for a token they cannot
 * verify — expired, revoked, or belonging to a deleted account. They swallow
 * the failure and return `false`, so Nest throws `ForbiddenException` with this
 * message, which the API's error formatter relays as code `FORBIDDEN`. The same
 * path covers a valid token whose account is not an ADMIN.
 *
 * The message has to be matched as well as the code: `FORBIDDEN` alone is also
 * what the API uses for ordinary business refusals ("Cannot delete another
 * user"), and those must not sign anyone out.
 */
export const GUARD_DENIAL_MESSAGE = 'Forbidden resource';

/** Raised by the API's global BanGuard on every operation a banned account attempts. */
const ACCOUNT_BANNED = 'ACCOUNT_BANNED';

/**
 * Whether a failed operation means the stored token is no longer good.
 *
 * `TokenExpiredError` is what this API returns for a lapsed token; the
 * `UNAUTHENTICATED` code and a 401 are covered as the standard equivalents.
 * A guard denial and a ban are covered too — see `GUARD_DENIAL_MESSAGE`.
 */
export function isSessionExpiredError({
  graphQLErrors,
  networkError,
}: ErrorResponse): boolean {
  const statusCode = (networkError as { statusCode?: number } | null)
    ?.statusCode;

  if (statusCode === 401) return true;

  return (graphQLErrors ?? []).some(
    ({ message, extensions }) =>
      message === 'TokenExpiredError' ||
      extensions?.code === 'UNAUTHENTICATED' ||
      extensions?.code === ACCOUNT_BANNED ||
      (extensions?.code === 'FORBIDDEN' && message === GUARD_DENIAL_MESSAGE),
  );
}

/**
 * The REST twin of `isSessionExpiredError`, for the CSV exports, whose guard
 * fails the same way: an HTTP 403 whose body carries `GUARD_DENIAL_MESSAGE`.
 *
 * Consumes the body on a 403, so call it only once the response is known to
 * be a failure.
 */
export async function isSessionExpiredResponse(
  response: Pick<Response, 'status' | 'json'>,
): Promise<boolean> {
  if (response.status === 401) return true;
  if (response.status !== 403) return false;

  try {
    const body = await response.json();

    return body?.message === GUARD_DENIAL_MESSAGE;
  } catch {
    return false;
  }
}

const listeners = new Set<Listener>();

/**
 * Subscribe to session expiry. Returns an unsubscribe function.
 */
export function onSessionExpired(listener: Listener): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/**
 * Announce that the stored token is no longer accepted by the API.
 *
 * Called from the Apollo error link, which runs outside React and so cannot
 * navigate on its own.
 */
export function notifySessionExpired(): void {
  listeners.forEach((listener) => listener());
}

/**
 * Clear the stored token and announce the expiry — at most once per session.
 *
 * Only meaningful while signed in. The login mutations run unauthenticated,
 * and must not bounce the user off the login screen they are already on.
 *
 * Clearing the token first also de-duplicates: an expired session usually
 * fails several in-flight operations at once, and the check stops the rest
 * from firing another redirect.
 */
export function expireSession(): void {
  if (!getTokens()) return;

  deleteTokens();
  notifySessionExpired();
}
